import { createContext, useContext, useState, ReactNode, Dispatch, SetStateAction, RefObject, useRef, useEffect } from 'react';

export type Track = {
  id: string | number;
  title: string;
  description: string;
  src: string;
  thumbnail?: string;
  duration?: number;
  isFavorite?: boolean;
}

interface AudioPlayerContextType {
  currentTrack: Track;
  setCurrentTrack: Dispatch<SetStateAction<Track>>;
  tracks: Array<Track>;
  setTracks: Dispatch<SetStateAction<Array<Track>>>;
  timeProgress: number;
  setTimeProgress: Dispatch<SetStateAction<number>>;
  duration: number;
  setDuration: Dispatch<SetStateAction<number>>;
  setTrackIndex: Dispatch<SetStateAction<number>>;
  audioRef: RefObject<HTMLAudioElement>;
  progressBarRef: RefObject<HTMLInputElement>;
  isPlaying: boolean;
  setIsPlaying: Dispatch<SetStateAction<boolean>>;
  playbackSpeed: number;
  setPlaybackSpeed: Dispatch<SetStateAction<number>>;
  volume: number;
  setVolume: Dispatch<SetStateAction<number>>;
  muteVolume: boolean;
  setMuteVolume: Dispatch<SetStateAction<boolean>>;
  lastProgressValue: number;
  setLastProgressValue: Dispatch<SetStateAction<number>>;
}

const AudioPlayerContext = createContext<AudioPlayerContextType>({} as AudioPlayerContextType);

export const AudioPlayerProvider = ({ children }: { children: ReactNode }) => {
  const [trackIndex, setTrackIndex] = useState<number>(0);
  const [currentTrack, setCurrentTrack] = useState<Track>({} as Track);
  const [tracks, setTracks] = useState<Array<Track>>([]);

  const [timeProgress, setTimeProgress] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
  const [volume, setVolume] = useState<number>(60);
  const [muteVolume, setMuteVolume] = useState<boolean>(false);
  const [lastProgressValue, setLastProgressValue] = useState<number>(0);

  const audioRef = useRef<HTMLAudioElement>(null);
  const progressBarRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setCurrentTrack(tracks[trackIndex]);
  }, [trackIndex]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = muteVolume ? 0 : volume / 100;
      audioRef.current.muted = muteVolume;
    }
  }, [volume, muteVolume, audioRef]);

  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement) return;

    const updateProgress = () => {
      setTimeProgress(audioElement.currentTime);
      setLastProgressValue(audioElement.currentTime);
    };

    const setAudioDuration = () => {
      setDuration(audioElement.duration);
      console.log(`Audio duration set: ${audioElement.duration}`);
    };

    audioElement.addEventListener('timeupdate', updateProgress);
    audioElement.addEventListener('loadedmetadata', setAudioDuration);

    return () => {
      audioElement.removeEventListener('timeupdate', updateProgress);
      audioElement.removeEventListener('loadedmetadata', setAudioDuration);
    };
  }, [audioRef, setTimeProgress, setDuration, setLastProgressValue]);
  useEffect(() => {
    if (progressBarRef.current) {
      console.log('ProgressBar Refffffff:', progressBarRef.current);
    }
  }, [progressBarRef]);

  const contextValue = {
    currentTrack,
    setCurrentTrack,
    tracks,
    setTracks,
    audioRef,
    progressBarRef,
    timeProgress,
    setTimeProgress,
    duration,
    setDuration,
    setTrackIndex,
    isPlaying,
    setIsPlaying,
    playbackSpeed,
    setPlaybackSpeed,
    volume,
    setVolume,
    muteVolume,
    setMuteVolume,
    lastProgressValue,
    setLastProgressValue,
  };

  return (
    <AudioPlayerContext.Provider value={contextValue}>
      {children}
    </AudioPlayerContext.Provider>
  );
};

export const useAudioPlayerContext = (): AudioPlayerContextType => {
  const context = useContext(AudioPlayerContext);

  if (context === undefined) {
    throw new Error(
      'useAudioPlayerContext must be used within an AudioPlayerProvider'
    );
  }

  return context;
};